import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Modal, Text } from "@wayflyer/flyui";
import { RhfRadioField } from "@wayflyer/flyui-hook-form";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import { MESSAGES } from "../message-definitions";
import type { BankingDataDeclarationChoice } from "../utils";

type BankingDataDeclarationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (choice: BankingDataDeclarationChoice) => void;
  requiredMonths: number;
  previousChoice: BankingDataDeclarationChoice | null;
  isPending: boolean;
};

const CHOICES: BankingDataDeclarationChoice[] = ["full_period_confirmed", "will_add_missing", "cannot_provide"];

const isChoice = (value: string): value is BankingDataDeclarationChoice => CHOICES.some((choice) => choice === value);

type BankingDataDeclarationFormValues = { choice: string };

export const BankingDataDeclarationModal = ({
  isOpen,
  onClose,
  onConfirm,
  requiredMonths,
  previousChoice,
  isPending,
}: BankingDataDeclarationModalProps) => {
  const intl = useIntl();

  const form = useForm<BankingDataDeclarationFormValues>({
    resolver: yupResolver(
      Yup.object({
        choice: Yup.string().nullable().required(intl.formatMessage(MESSAGES.bankingDataDeclarationNoSelectionError)),
      })
    ),
    values: { choice: previousChoice ?? "" },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleConfirm = form.handleSubmit(({ choice }) => {
    if (choice && isChoice(choice)) {
      onConfirm(choice);
    }
  });

  return (
    <Modal
      testId="banking-data-declaration-modal"
      isOpen={isOpen}
      onClose={handleClose}
      title={intl.formatMessage(MESSAGES.bankingDataDeclarationModalTitle)}
      size="small"
      primaryAction={{
        content: intl.formatMessage(MESSAGES.bankingDataDeclarationConfirmAction),
        onAction: handleConfirm,
        loading: isPending,
      }}
    >
      <Flex direction="column" gap="6">
        <Text textWrap="pretty">
          {intl.formatMessage(MESSAGES.bankingDataDeclarationModalBody, { requiredMonths })}
        </Text>
        <RhfRadioField
          form={form}
          name="choice"
          // null stops the radio group pre-selecting its first option
          defaultValue={previousChoice}
          label={intl.formatMessage(MESSAGES.bankingDataDeclarationModalQuestion)}
          options={[
            {
              value: "full_period_confirmed",
              label: intl.formatMessage(MESSAGES.bankingDataDeclarationOptionConfirmed, { requiredMonths }),
            },
            {
              value: "will_add_missing",
              label: intl.formatMessage(MESSAGES.bankingDataDeclarationOptionWillAdd),
            },
            {
              value: "cannot_provide",
              label: intl.formatMessage(MESSAGES.bankingDataDeclarationOptionCannotProvide, { requiredMonths }),
            },
          ]}
        />
      </Flex>
    </Modal>
  );
};
